import React from 'react';
import ErrorBoundary from './ErrorBoundary';
import { useConnectionState, useToastState } from '../../store/useAppStore';

interface ConnectionErrorBoundaryProps {
  children: React.ReactNode;
}

const ConnectionErrorBoundary: React.FC<ConnectionErrorBoundaryProps> = ({ children }) => {
  const { isConnected, authId } = useConnectionState();
  const { showToast } = useToastState();

  const handleConnectionError = (error: Error, errorInfo: React.ErrorInfo) => {
    // Log connection-specific error
    console.error('Connection Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      isConnected,
      authId,
      timestamp: new Date().toISOString()
    });

    // Show specific toast for connection errors
    showToast({
      type: 'error',
      title: 'Erro de Conexão',
      message: 'Problema na conexão com o WhatsApp. Verifique sua internet e reconecte.',
      duration: 10000,
      action: {
        label: 'Reconectar',
        onClick: () => {
          // Clear auth and reload
          localStorage.removeItem('wpp_auth_uuid');
          window.location.reload();
        }
      }
    });
  };

  const ConnectionErrorFallback = () => (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '300px',
      padding: '2rem',
      textAlign: 'center',
      backgroundColor: '#fef3c7',
      borderRadius: '12px',
      margin: '1rem',
      border: '2px solid #fbbf24'
    }}>
      <div style={{
        fontSize: '3rem',
        marginBottom: '1rem'
      }}>
        📡❌
      </div>
      
      <h3 style={{
        fontSize: '1.25rem',
        fontWeight: 'bold',
        color: '#92400e',
        marginBottom: '0.5rem'
      }}>
        Erro de Conexão
      </h3>
      
      <p style={{
        color: '#78350f',
        marginBottom: '1.5rem',
        maxWidth: '300px'
      }}>
        Não foi possível conectar ao WhatsApp. Verifique sua conexão com a internet.
      </p>

      <div style={{
        display: 'flex',
        gap: '0.75rem',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <button
          onClick={() => {
            localStorage.removeItem('wpp_auth_uuid');
            window.location.reload();
          }}
          style={{
            backgroundColor: '#f59e0b',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            padding: '0.75rem 1.5rem',
            fontWeight: '600',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}
        >
          🔄 Reconectar WhatsApp
        </button>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem',
          marginTop: '1rem',
          fontSize: '0.875rem',
          color: '#78350f'
        }}>
          <div>✓ Verifique sua conexão com a internet</div>
          <div>✓ Certifique-se de que o WhatsApp Web está funcionando</div>
          <div>✓ Tente usar outro navegador se o problema persistir</div>
        </div>
      </div>

      <div style={{
        marginTop: '1.5rem',
        padding: '0.75rem',
        backgroundColor: '#fed7aa',
        borderRadius: '6px',
        fontSize: '0.75rem',
        color: '#9a3412',
        maxWidth: '400px'
      }}>
        <strong>Status:</strong> {isConnected ? 'Conectado' : 'Desconectado'}<br />
        <strong>Auth ID:</strong> {authId ? authId.substring(0, 8) + '...' : 'Não definido'}
      </div>
    </div>
  );

  return (
    <ErrorBoundary onError={handleConnectionError} fallback={<ConnectionErrorFallback />}>
      {children}
    </ErrorBoundary>
  );
};

export default ConnectionErrorBoundary;